import { useEffect, useMemo, useRef, useState } from "react";
import { GriddEmbed, type GriddSnapshot } from "@gridd/embed/react";
import {
  BarChart3,
  Eraser,
  GanttChartSquare,
  Grid3x3,
  ImagePlus,
  PenLine,
  Type,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { ChartBlock } from "@/components/deck/ChartBlock";
import { DiagramBlock } from "@/components/deck/DiagramBlock";
import { GanttBlock } from "@/components/deck/GanttBlock";
import { ImageBlock } from "@/components/deck/ImageBlock";
import { ImageEditDialog } from "@/components/deck/ImageEditDialog";
import { useSnapshotSourceRef } from "@/components/deck/snapshotRegistry";
import { useScheduleThumbnail } from "@/components/deck/thumbnailStore";
import { TextBlock } from "@/components/deck/TextBlock";
import { useElementSize } from "@/hooks/useElementSize";
import { useImageAsset } from "@/hooks/useImageAsset";
import { GRIDD_EMBED_BASE_URL } from "@/lib/config";
import { dropContentArtifacts } from "@/lib/contentCleanup";
import { createChartDoc } from "@/lib/chart";
import { loadChartCanvas } from "@/lib/chart-engine";
import { createDiagramDoc } from "@/lib/diagram";
import { loadDiagramEngine } from "@/lib/diagram-engine";
import { createGanttDoc } from "@/lib/gantt";
import { loadGanttCanvas } from "@/lib/gantt-engine";
import { seedGriddDocument } from "@/lib/griddSeed";
import { imageBackground } from "@/lib/image";
import { putAsset } from "@/lib/idb";
import { resolveRoleTypography } from "@/lib/stylesheet-typography";
import { emptyTextDoc } from "@/lib/textSeed";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { chartDocCreated } from "@/store/chartSlice";
import { diagramDocCreated } from "@/store/diagramSlice";
import { ganttDocCreated } from "@/store/ganttSlice";
import { griddDocCreated, griddDocUpdated } from "@/store/griddSlice";
import { selectEffectiveStyleSheet } from "@/store/styleDraftSlice";
import type {
  ImageCrop,
  ImageFilters,
  ImageTransform,
  SlideContent,
} from "@/types/content";
import type { GriddDocument } from "@/types/document";

// One content window on a slide (TODO §4.2). Self-contained: it owns the empty-state
// placeholder button row, the Gridd/text/image editor when filled, and the per-window
// clear that runs the orphan cleanup. A slide renders one of these (single layout) or
// two side by side / stacked (two-* layouts); each window is independent.
//
// `hasGridd` is true when the *slide* already holds a Gridd in any window — the
// one-Gridd rule (§4.3): a window only offers "Add Gridd" when the slide has none yet.
export function ContentWindow({
  content,
  onChange,
  readOnly,
  hasGridd,
  owner,
  shared = false,
  style,
  snapshots,
}: {
  content: SlideContent;
  onChange: (content: SlideContent) => void;
  readOnly: boolean;
  hasGridd: boolean;
  // §17.1.3 Which owner key a body created in this window is filed under — the deck's, or the
  // NUGGET's when this slide is a linked one. Passed down from `Slide`, which is the component
  // that can see the slide's `link`; everything below here only knows about one window.
  //
  // The three Redux-backed bodies resolve their owner in the persistence listener instead
  // (`ownerForContentDoc`) and do not need this. An image blob does: `putAsset` is called
  // straight from this file, with no action for a listener to intercept.
  owner: string;
  // §17.3.1 True when this window's body is a nugget's — shared with every other deck that
  // links it. Drives the re-bake gate on `onDocumentChange` below; see the long note there.
  shared?: boolean;
  // Sizing from the parent — the two-* layouts pass a `flexGrow` to split by ratio
  // (§4.7). Merged onto the window root, whose `flex-1` class supplies flex-basis: 0.
  style?: React.CSSProperties;
  // Present only on the print path (TODO §6.6.4): a griddDocId→snapshot map. When this
  // window's grid has an entry, render the static snapshot instead of a live iframe.
  snapshots?: Record<string, GriddSnapshot>;
}) {
  const dispatch = useAppDispatch();

  // §21/§3.1 This window's own LOGICAL size. `useElementSize` reads `clientWidth`/
  // `clientHeight`, which the slide's `scale()` transform does not affect, so the numbers
  // are already in the 1600×900 canvas's units — which is what a new diagram's frame is
  // authored against, with no scale plumbing.
  const [windowRef, windowSize] = useElementSize<HTMLDivElement>();

  // The grid body lives in griddSlice, keyed by this window's own griddDocId — so two
  // windows on the same slide never read each other's doc (§4.5, no doc bleed).
  const griddDocId = content?.kind === "gridd" ? content.griddDocId : null;
  const griddDoc = useAppSelector((s) =>
    griddDocId ? s.gridd[griddDocId] : undefined,
  );

  // Register this embed's imperative handle so the export flow can pull a print snapshot of
  // the live (editor) grid (§6.6.4). No-op outside the snapshot provider.
  const embedRef = useSnapshotSourceRef(griddDocId);
  // Deferred nav-thumbnail capture: schedule one when this grid first mounts (below) and after
  // every edit (in onDocumentChange). No-op outside the editor's thumbnail provider.
  const scheduleThumbnail = useScheduleThumbnail();
  // Kick off the initial thumbnail once the grid is present. The scheduler defers to idle and
  // retries until the embed has loaded, so this lands even though the iframe isn't ready yet.
  useEffect(() => {
    if (griddDocId) scheduleThumbnail(griddDocId);
  }, [griddDocId, scheduleThumbnail]);
  // A pre-captured snapshot for this grid (print path only). When set, we render it as a
  // static image and never mount the iframe (§6.6.4); absent → fall back to the live embed.
  const snapshot = griddDocId ? snapshots?.[griddDocId] : undefined;

  // Image blobs live in the `assets` IndexedDB store keyed by assetId — never in Redux
  // (TODO §3.2). The deckId is the cascade-cleanup key on each asset record.
  const deckId = useAppSelector((s) => s.document.id);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingImage, setEditingImage] = useState(false);
  const imageAssetId = content?.kind === "image" ? content.assetId : undefined;
  // Only materialize an object URL for the editor while it's actually open.
  const editSrc = useImageAsset(editingImage ? imageAssetId : undefined);

  // draft ?? committed — pushed live into every embed so stylesheet-editor previews
  // restyle all grids without a reload (TODO §8.1/§10.1). Controlled, unlike document.
  const effectiveStyleSheet = useAppSelector(selectEffectiveStyleSheet);
  // Text blocks default their typography from the same effective stylesheet the grids
  // render from, so a sheet change restyles text like it restyles grids (TODO §2.4).
  // They resolve the sheet's *Body* role — running text — the same way the slide chrome
  // resolves header/subheader/highlight, so authoring "Body" in the sheet restyles every
  // text block. With no such preset the role falls back to the sheet's base typography.
  const textTypography = useMemo(
    () => resolveRoleTypography(effectiveStyleSheet, "body"),
    [effectiveStyleSheet],
  );

  // === §17.3 THE STYLESHEET TRAP, and the gate that closes it ===
  //
  // Read §17.3 before touching this. A grid body carries **baked** hex and pt values; the
  // embed re-bakes them whenever a stylesheet is pushed to it, and that re-bake trips the same
  // ~400ms `onDocumentChange` a keystroke does. So a nugget authored in deck A (sheet X),
  // merely OPENED in deck B (sheet Y), emits a save carrying B's baked values — into the body
  // A and three other decks share. Every one of them silently restyles. Nobody touched
  // anything. The `styleDraft` suppression in the persistence listener covers *preview* only,
  // which is a different event entirely.
  //
  // The gate is §17.3.1's: an `onDocumentChange` arriving after a sheet push with no
  // intervening user edit in THAT embed is a re-bake, not an edit, and must not reach the
  // nugget. What follows is the cheapest honest signal for "the user has been in this embed"
  // that a host page can get from a cross-origin iframe: **focus**. Nothing inside the frame is
  // observable from here — no keystrokes, no clicks — but the moment a click or a keypress
  // lands in it, the iframe takes focus and this window emits `blur`. That is a real
  // interaction every time, and it is set BEFORE the ~400ms debounce fires.
  //
  // The flag is cleared on every sheet identity change, which is exactly what a push is.
  //
  // Deck-owned bodies are untouched by any of this (`shared` is false): they restyle on a
  // re-bake, as they always have, because the body belongs to the deck whose sheet just
  // changed. §17.3.2 — the nugget does not carry a sheet of its own and adopts the host
  // deck's like a Gridd Library slide does. It is only the WRITE-BACK that is suppressed,
  // never the render, so a shared slide still LOOKS right in every deck that uses it.
  const interactedRef = useRef(false);
  useEffect(() => {
    interactedRef.current = false;
  }, [effectiveStyleSheet]);
  useEffect(() => {
    if (!shared || !griddDocId) return;
    const onBlur = () => {
      // The activeElement at the moment the window loses focus is the iframe that took it.
      // Any other blur (another app, the URL bar) leaves it unchanged or on <body>.
      const active = document.activeElement;
      if (
        active instanceof HTMLIFrameElement &&
        windowRef.current?.contains(active)
      ) {
        interactedRef.current = true;
      }
    };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, [shared, griddDocId, windowRef]);

  // Add Gridd: point this window at a fresh docId, THEN create the seeded body. Two
  // dispatches, two persisted records (the slide via putDeck, the grid via putGriddDoc).
  //
  // §17.2.3 **The order is load-bearing and it used to be the other way round.** The
  // persistence listener resolves a body's owner by finding the slide that holds its docId
  // (`ownerForContentDoc`) — so with the body created first, the listener runs against a state
  // in which no slide points at it yet, falls through to the deck, and a grid added to a
  // LINKED slide is filed under this deck instead of under the nugget. Redux dispatches are
  // synchronous, so putting `onChange` first means the slide already holds the id by the time
  // the listener looks. The same flip applies to the two below.
  const addGridd = () => {
    const docId = crypto.randomUUID();
    onChange({ kind: "gridd", griddDocId: docId });
    dispatch(griddDocCreated({ docId, doc: seedGriddDocument() }));
  };

  // §21/§3.1 Add diagram: create a scene holding ONE frame, sized to this window's logical
  // rect, then point the window at it. The Excalidraw chunk is fetched here and not before
  // (§10.3) — this click is the first moment the app needs it, and a deck with no diagram
  // never reaches this line.
  //
  // The frame is seeded from the COMMITTED stylesheet, not the effective one: a live
  // stylesheet preview is uncommitted, and §9.1 means a diagram is never restyled
  // afterwards, so a diagram born inside a preview the user then cancels would keep colours
  // from a sheet that was never saved.
  const [busy, setBusy] = useState(false);
  const committedStyleSheet = useAppSelector((s) => s.document.styleSheet);
  const addDiagram = async () => {
    setBusy(true);
    try {
      const engine = await loadDiagramEngine();
      const docId = crypto.randomUUID();
      // §17.2.3 Window first, body second — see `addGridd`.
      onChange({ kind: "diagram", diagramDocId: docId });
      dispatch(
        diagramDocCreated({
          docId,
          doc: createDiagramDoc(engine, windowSize, committedStyleSheet),
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  // §26/§3.4 Add gantt: seed a small plausible plan, then point this window at it. The
  // widget chunk is fetched here and not before (§7.1) — this click is the first moment the
  // app needs it, and a deck with no gantt never reaches this line.
  //
  // Unlike a diagram the seed needs neither the window size nor the stylesheet: the chart is
  // FITTED to whatever window it finds itself in on every render (§4.3), and its colours are
  // resolved live from the effective sheet by `GanttBlock`. There is nothing baked in at
  // creation time that a later layout or sheet change could make wrong.
  const addGantt = async () => {
    setBusy(true);
    try {
      await loadGanttCanvas();
      const docId = crypto.randomUUID();
      // §17.2.3 Window first, body second — see `addGridd`.
      onChange({ kind: "gantt", ganttDocId: docId });
      dispatch(ganttDocCreated({ docId, doc: createGanttDoc() }));
    } finally {
      setBusy(false);
    }
  };

  // §15/§4.6 Add chart: seed a small plausible table, then point this window at it. The
  // Recharts chunk is fetched here and not before (§2.4) — this click is the first moment the
  // app needs it, and a deck with no chart never reaches this line.
  //
  // Like a gantt and unlike a diagram, the seed needs neither the window size nor the
  // stylesheet: the chart is laid out into whatever window it finds itself in on every render
  // (§8.2), and its colours are resolved live from the effective sheet by `ChartBlock` (§6.2).
  // There is nothing baked in at creation time that a later layout or sheet change could make
  // wrong — which is the whole point of §6.2's name indirection.
  const addChart = async () => {
    setBusy(true);
    try {
      await loadChartCanvas();
      const docId = crypto.randomUUID();
      // §17.2.3 Window first, body second — see `addGridd`.
      onChange({ kind: "chart", chartDocId: docId });
      dispatch(chartDocCreated({ docId, doc: createChartDoc() }));
    } finally {
      setBusy(false);
    }
  };

  // Add text: seed a blank TipTap doc inline on the content record (TODO §2.2/§2.3).
  const addText = () => {
    onChange({ kind: "text", json: emptyTextDoc() });
  };

  // The text block is uncontrolled after mount, so keystroke edits arrive here already
  // debounced (TODO §2.3); write straight through to the inline content record.
  const updateText = (json: unknown) => {
    onChange({ kind: "text", json });
  };

  // Add image: stash the picked file's blob in IndexedDB (never in Redux, TODO §3.2),
  // reference it by assetId on the content record, then open the crop editor for raster
  // images. SVG is passed through as-is — raster edits don't apply (TODO §3.1).
  const onImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same file still fires change
    if (!file) return;
    const assetId = crypto.randomUUID();
    // §17.1.3 Under the slide's OWNER, which is the nugget on a linked slide. An image
    // dropped onto a shared slide belongs to the nugget like everything else on it.
    await putAsset(assetId, owner, file, file.type);
    onChange({ kind: "image", assetId, mime: file.type });
    if (file.type !== "image/svg+xml") setEditingImage(true);
  };

  // Crop/transform/filter edits are stored as data on the content record and re-applied
  // at render (TODO §3.3) — the blob is never re-encoded. `crop` is optional and an absent
  // one is written through as absent, which is how "the user un-cropped it" is expressed:
  // `effectiveFit` then renders the image whole instead of filling the window (B4).
  const updateImage = (patch: {
    crop?: ImageCrop;
    transform: ImageTransform;
    filters?: ImageFilters;
    background?: string;
  }) => {
    if (content?.kind !== "image") return;
    onChange({ ...content, ...patch });
  };

  // Clear just this window: drop its grid body / image asset so nothing orphans, then
  // empty the window back to the placeholder (TODO §4.6, orphan rule).
  const clearWindow = () => {
    dropContentArtifacts(dispatch, content, deckId);
    onChange(null);
  };

  return (
    <div
      ref={windowRef}
      className="group relative flex min-h-0 min-w-0 flex-1 flex-col"
      style={style}
    >
      {content?.kind === "gridd" && griddDoc ? (
        snapshot ? (
          // Print path (§6.6.4): a static, self-contained snapshot of the live grid stands
          // in for the iframe so the page prints reliably. Its aspect matches the window
          // (same logical layout/split as the editor), so object-contain fills it exactly.
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden rounded-md border border-slate-200">
            <img
              src={snapshot.dataUrl}
              alt=""
              className="h-full w-full object-contain"
            />
          </div>
        ) : (
          // The iframe requests its data exactly once at load, so the embed is
          // uncontrolled after mount — key it on docId so switching docs remounts, and
          // never feed onDocumentChange output back in as `document` (TODO §5.7). Moving a
          // Gridd between windows (or a layout change that shifts its DOM slot) remounts
          // it — acceptable, the doc reloads from Redux (§4.5). Each embed is a full Gridd
          // app instance; watch memory with several windows (§5.9). `embedRef` registers
          // its handle for print snapshots (§6.6.4).
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden rounded-md border border-slate-200">
            <GriddEmbed
              key={content.griddDocId}
              ref={embedRef}
              baseUrl={GRIDD_EMBED_BASE_URL}
              // The embed types the document loosely as Record<string, unknown>; our
              // GriddDocument is a specific shape, so cross the boundary via unknown.
              document={griddDoc as unknown as Record<string, unknown>}
              styleSheet={effectiveStyleSheet}
              readOnly={readOnly}
              width="100%"
              height="100%"
              onDocumentChange={(doc) => {
                // §17.3.1 A shared body takes no write until this embed has actually been
                // interacted with since the last sheet push — see the long note above. The
                // change is dropped entirely rather than dispatched-and-not-persisted:
                // letting a re-bake into Redux would leave the store holding this deck's
                // baked colours, which the very next real edit would then carry into the
                // nugget as if the user had chosen them.
                if (shared && !interactedRef.current) return;
                dispatch(
                  griddDocUpdated({
                    docId: content.griddDocId,
                    doc: doc as unknown as GriddDocument,
                  }),
                );
                // Refresh the nav thumbnail off this now-edited grid (deferred/idle).
                scheduleThumbnail(content.griddDocId);
              }}
            />
          </div>
        )
      ) : content?.kind === "text" ? (
        // Text lives inline on the content record. Uncontrolled after mount (like the
        // Gridd embed): `json` seeds it once and edits flow out debounced (TODO §2.3).
        <TextBlock
          json={content.json}
          onChange={updateText}
          readOnly={readOnly}
          typography={textTypography}
        />
      ) : content?.kind === "diagram" ? (
        // §5.1 A static SVG at rest; the live editor mounts only for the window being
        // edited, and only inside DeckEditor's DiagramEditingProvider — so the print,
        // presentation and off-screen-thumbnail copies of this component cannot mount one.
        <DiagramBlock docId={content.diagramDocId} readOnly={readOnly} />
      ) : content?.kind === "gantt" ? (
        // §26/§2 Always live — the widget IS the renderer. `readOnly` reaches SVAR's own
        // `readonly` prop, so the presentation, print and thumbnail copies are inert.
        <GanttBlock docId={content.ganttDocId} readOnly={readOnly} />
      ) : content?.kind === "chart" ? (
        // §15/§8.1 Always live — we own the renderer, so there is no at-rest form to cache.
        // `readOnly` reaches the toggle, the tooltip, the animation and the accessibility
        // layer, so the presentation, print and thumbnail copies are inert pictures.
        <ChartBlock docId={content.chartDocId} readOnly={readOnly} />
      ) : content?.kind === "image" ? (
        // Image blob loads on demand from IndexedDB; crop/transform/filters apply at
        // render (TODO §3.3/§3.6). Edit re-opens the crop dialog.
        <ImageBlock
          content={content}
          // §22.2 Name → hex here, where the effective sheet already is, so a stylesheet
          // preview recolours the window live like it recolours the grids.
          background={imageBackground(effectiveStyleSheet, content)}
          readOnly={readOnly}
          onEdit={() => setEditingImage(true)}
        />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-200 bg-slate-50/60 p-4">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              variant="default"
              size="lg"
              onClick={addGridd}
              // The one-Gridd rule (§4.3): a slide holds at most one Gridd, so this
              // window offers it only when no window has one yet.
              disabled={readOnly || hasGridd}
            >
              <Grid3x3 className="mr-2 h-4 w-4" /> Add Gridd
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => fileInputRef.current?.click()}
              disabled={readOnly}
            >
              <ImagePlus className="mr-2 h-4 w-4" /> Add image
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={addText}
              disabled={readOnly}
            >
              <Type className="mr-2 h-4 w-4" /> Add text
            </Button>
            {/* §21/§14 Add diagram. No one-per-slide rule (unlike Gridd): a diagram is a
                same-origin React component, not a cross-origin iframe, so two on a slide
                cost two SVG strings rather than two full app instances. */}
            <Button
              variant="secondary"
              size="lg"
              onClick={() => void addDiagram()}
              disabled={readOnly || busy}
            >
              <PenLine className="mr-2 h-4 w-4" /> Add diagram
            </Button>
            {/* §26/§2 Add gantt. No one-per-slide rule, for the same reason diagrams have
                none: it is a same-origin React component, not an app in an iframe. */}
            <Button
              variant="secondary"
              size="lg"
              onClick={() => void addGantt()}
              disabled={readOnly || busy}
            >
              <GanttChartSquare className="mr-2 h-4 w-4" /> Add gantt
            </Button>
            {/* §15 Add chart. No one-per-slide rule, for the reason diagrams and gantts have
                none: it is a same-origin React component, not an app in an iframe, so two on a
                slide cost two SVG trees rather than two full app instances (§1). */}
            <Button
              variant="secondary"
              size="lg"
              onClick={() => void addChart()}
              disabled={readOnly || busy}
            >
              <BarChart3 className="mr-2 h-4 w-4" /> Add chart
            </Button>
          </div>
          {hasGridd && (
            <p className="text-center text-xs text-slate-400">
              This slide already has a Gridd (one per slide) — add text, an
              image, a diagram, a gantt or a chart here.
            </p>
          )}
        </div>
      )}

      {/* Per-window clear, shown only on a filled, editable window. Top-right in every
          case — a Gridd embed renders its own controls bottom-right, so the old
          bottom-right placement overlapped them. The image "Edit" overlay (also top-right)
          shifts left to make room for this. */}
      {content && !readOnly && (
        <Button
          variant="secondary"
          size="sm"
          onClick={clearWindow}
          aria-label="Clear window"
          title="Clear window"
          className="absolute right-2 top-8 z-10 shadow"
        >
          <Eraser className="h-4 w-4" />
        </Button>
      )}

      {/* Hidden (display:none) so the slide's scale transform is irrelevant; the dialog
          portals to <body>, so it isn't scaled either. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onImageFile}
      />
      {editingImage && editSrc && content?.kind === "image" && (
        <ImageEditDialog
          open={editingImage}
          onOpenChange={setEditingImage}
          src={editSrc}
          // §22.2 A vector opens the same dialog in its reduced form (background + filter):
          // it is the only surface those two controls have.
          isSvg={content.mime === "image/svg+xml"}
          initialCrop={content.crop}
          initialTransform={content.transform}
          initialFilters={content.filters}
          initialBackground={content.background}
          onSave={updateImage}
        />
      )}
    </div>
  );
}
